import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip } from "recharts";
import { api, get, errMsg } from "@/lib/api";
import { ChartTooltip, GRID } from "@/lib/charts";
import { cn } from "@/lib/utils";

export function CalibrationPanel({ job }) {
  const qc = useQueryClient();
  const { data: ranked } = useQuery({ queryKey: ["ranked", job.id], queryFn: () => get(`/jobs/${job.id}/ranked`) });
  const { data: cal } = useQuery({ queryKey: ["calibration", job.id], queryFn: () => get(`/jobs/${job.id}/calibration`) });
  const rows = (ranked?.rows || []).filter((r) => r.resume_match != null);
  const sample = [...rows.slice(0, 5), ...rows.slice(-5)].filter((r, i, a) => a.findIndex((x) => x.application_id === r.application_id) === i);
  const label = async (r, value) => {
    try {
      await api.post(`/jobs/${job.id}/applications/${r.application_id}/calibration`, { label: r.calibration === value ? null : value });
      qc.invalidateQueries({ queryKey: ["ranked", job.id] });
      qc.invalidateQueries({ queryKey: ["calibration", job.id] });
      toast.success("Label saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel space-y-4 p-6" data-testid="calibration-panel">
      <div>
        <h3 className="text-base font-semibold">Calibrate scoring</h3>
        <p className="text-sm text-ink-secondary">Mark 5–10 candidates as good or bad fits. We compare your judgement with the AI (good = Resume Match ≥ {cal?.cutoff ?? 55}).</p>
      </div>
      <p className="font-display text-2xl font-semibold" data-testid="calibration-agreement">{cal?.agreement_pct != null ? `${cal.agreement_pct}% agreement` : "No labels yet"}<span className="ml-2 text-sm font-normal text-ink-muted">{cal?.labelled ? `${cal.agree} of ${cal.labelled}` : ""}</span></p>
      <div className="grid gap-4 lg:grid-cols-2">
        <ul className="divide-y divide-line rounded-[6px] border border-line">
          {sample.map((r) => (
            <li key={r.application_id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="flex-1 truncate">{r.name}</span><span className="font-display font-semibold tabular-nums">{r.resume_match}</span>
              {[["good", ThumbsUp, "text-success"], ["bad", ThumbsDown, "text-risk"]].map(([v, Icon, tone]) => (
                <button key={v} onClick={() => label(r, v)} aria-label={v} data-testid={`calibrate-${v}-${r.application_id}`}
                  className={cn("rounded-lg border p-1.5 transition-colors duration-150", r.calibration === v ? `border-current ${tone} bg-subtle` : "border-line text-ink-muted hover:text-ink")}><Icon size={14} /></button>
              ))}
            </li>
          ))}
        </ul>
        <ResponsiveContainer width="100%" height={200} initialDimension={{ width: 320, height: 200 }}>
          <ScatterChart>
            <CartesianGrid stroke={GRID} />
            <XAxis type="number" dataKey="score" name="Resume Match" domain={[0, 100]} tick={{ fontSize: 11, fill: "#8A8D85" }} />
            <YAxis type="number" dataKey="y" domain={[0, 1]} ticks={[0, 1]} tickFormatter={(v) => (v ? "Good" : "Bad")} tick={{ fontSize: 11, fill: "#8A8D85" }} width={40} />
            <ReferenceLine x={cal?.cutoff ?? 55} stroke="#9A6700" strokeDasharray="4 3" />
            <Tooltip content={<ChartTooltip />} />
            <Scatter data={(cal?.points || []).map((p) => ({ ...p, y: p.label === "good" ? 1 : 0 }))} name="Labels" fill="#35594A" isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
